// src/pages/UnitDetailsPage.jsx
import React, { useEffect, useState } from "react";
import { Link as RouterLink, useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBreadcrumb } from "../context/BreadcrumbContext";
import Layout from "../components/layout/Layout";
import InviteOwnersButton from "../components/client/InviteOwnersButton";
import { formatDateUk } from "../utils/dateDisplay";

import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  Skeleton,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Link,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import {
  getUnitDetails,
  getAssessmentTypes,
  getPropertyById,
  getUnitNeighbours,
  getUnitOwnerships,
  changeUnitOwner,
  updateUnitOwnership,
} from "../api/unitApi";

import {
  getLPE1ListForUnit,
} from "../api/lpe1Api";
import { getFME1ListForUnit } from "../api/fme1Api";
import { getUnitTransferHandover } from "../api/financeApi";

const TAB_KEYS = ["overview", "enquiries", "utilities", "assessments", "insurance", "ownership"];

// Helper to get document status based on expiry/renewal date
const getDocumentStatus = (dateStr) => {
  if (!dateStr) return { status: "unknown", label: "No date", color: "default" };
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryDate = new Date(dateStr);
  expiryDate.setHours(0, 0, 0, 0);
  
  const diffDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    return { status: "expired", label: "Expired", color: "error", icon: <ErrorIcon fontSize="small" /> };
  } else if (diffDays <= 90) {
    return { status: "expiring", label: `${diffDays}d left`, color: "warning", icon: <WarningAmberIcon fontSize="small" /> };
  } else {
    return { status: "valid", label: "Valid", color: "success", icon: <CheckCircleIcon fontSize="small" /> };
  }
};

function UnitDetailsPage() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useAuth();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState(null);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [lpe1Forms, setLpe1Forms] = useState([]);
  const [fme1Forms, setFme1Forms] = useState([]);
  const [recentOwnerships, setRecentOwnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [neighborUnitIds, setNeighborUnitIds] = useState({ prev: null, next: null });
  const [activeTab, setActiveTab] = useState(() => Math.max(TAB_KEYS.indexOf(searchParams.get("tab")), 0));

  useEffect(() => {
    setActiveTab(Math.max(TAB_KEYS.indexOf(searchParams.get("tab")), 0));
  }, [searchParams]);

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  useEffect(() => {
    const incomingToast = location.state?.toast;
    if (!incomingToast?.message) return;
    showToast(incomingToast.message, incomingToast.severity || "success");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const userRoles = user?.claims_role || [];
  const canEdit = userRoles.some(
    (r) =>
      permissions?.[r?.toLowerCase?.()]?.includes("PUT") ||
      permissions?.[r?.toLowerCase?.()]?.includes("PATCH")
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setNeighborUnitIds({ prev: null, next: null });
        const [unitData, typesData, lpe1Data, fme1Data, neighboursData, ownershipData] = await Promise.all([
          getUnitDetails(unitId),
          getAssessmentTypes(),
          getLPE1ListForUnit(unitId),
          getFME1ListForUnit(unitId).catch(() => []),
          getUnitNeighbours(unitId).catch(() => null),
          getUnitOwnerships(unitId, 1, 5).catch(() => ({ results: [] })),
        ]);

        setData(unitData || null);
        setAssessmentTypes(typesData || []);
        setLpe1Forms(lpe1Data || []);
        setFme1Forms(fme1Data || []);
        setRecentOwnerships(ownershipData?.results || []);
        setNeighborUnitIds({
          prev: neighboursData?.prev_id ?? null,
          next: neighboursData?.next_id ?? null,
        });

        // Fetch parent property for breadcrumb
        if (unitData?.unit?.property_id) {
          try {
            const propertyData = await getPropertyById(unitData.unit.property_id);
            
            // Set custom breadcrumb path: Properties > PropertyName > UnitName
            setCustomPath([
              { label: "Properties", path: "/properties" },
              { label: propertyData.name, path: `/properties/${unitData.unit.property_id}/details` },
              {
                label: `${
                  unitData.unit.unit_name ||
                  unitData.unit.unit_code ||
                  unitData.unit.id
                }`,
                path: null,
              },
            ]);
          } catch (propErr) {
            console.error("Failed to load property for breadcrumb:", propErr);
            // Fallback: just show unit name
            setCustomPath([
              { label: "Units", path: null },
              {
                label: `Unit ${
                  unitData.unit.unit_name ||
                  unitData.unit.unit_code ||
                  unitData.unit.id
                }`,
                path: null,
              },
            ]);
          }
        }
      } catch (err) {
        console.error("Error loading unit:", err);
        setError("Failed to load unit details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Cleanup: clear the custom path when leaving the page
    return () => {
      clearCustomPath();
    };
  }, [unitId, setCustomPath, clearCustomPath]);

  const formatDate = formatDateUk;

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return "N/A";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  };

  const formatSqMeters = (squareFeet) => {
    if (!squareFeet) return "";
    const meters = Number(squareFeet) * 0.092903;
    if (Number.isNaN(meters)) return "";
    return ` (${meters.toFixed(2)} sqm)`;
  };

  const detailTableSx = {
    width: "100%",
    borderCollapse: "collapse",
    "& th": {
      textAlign: "left",
      fontWeight: 600,
      paddingRight: 1,
      verticalAlign: "top",
      whiteSpace: "nowrap",
    },
    "& td": {
      paddingBottom: 0.75,
    },
    "& tr:last-of-type td": {
      paddingBottom: 0,
    },
  };

  const getAssessmentType = (typeId) => {
    const type = assessmentTypes.find((t) => t.id === typeId);
    return type ? type.name : "Unknown Type";
  };

  // --------------------------------------------------
  // CREATE NEW LPE1 + NAVIGATE
  // --------------------------------------------------
  const handleGenerateLPE1 = () => {
    // Navigate to new LPE1 form without creating database record
    // The record will be created when user clicks Save or Export
    navigate(`/lpe1/new?unit_id=${unitId}`, { state: { from: location.pathname } });
  };

  const handleGenerateFME1 = () => {
    navigate(`/fme1/new?unit_id=${unitId}`, { state: { from: location.pathname } });
  };

  const handleCopy = async (value, label) => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      showToast(`${label} copied`);
    } catch (err) {
      console.error(err);
      showToast("Copy failed.", "error");
    }
  };

  const buildAddress = (parts) =>
    parts
      .filter((value) => value && String(value).trim())
      .map((value) => String(value).trim())
      .join("\n");

  if (loading)
    return (
      <Layout>
        <Box className="page-container">
          <Skeleton variant="text" width={250} height={40} />
          <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
        </Box>
      </Layout>
    );

  if (error)
    return (
      <Layout>
        <Box className="page-container">
          <Alert severity="error">{error}</Alert>
        </Box>
      </Layout>
    );

  if (!data || !data.unit)
    return (
      <Layout>
        <Box className="page-container">
          <Alert severity="warning">No unit details found.</Alert>
        </Box>
      </Layout>
    );

  const { unit, utilities, insurance_policies, assessments } = data;

  return (
    <Layout>
      <Box className="page-container">
        {/* HEADER */}
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h4" fontWeight={600}>
            {unit.unit_name || unit.unit_code || unit.id}
          </Typography>
        </Grid>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(event, value) => setSearchParams({ tab: TAB_KEYS[value] })}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Tab label="Overview" />
            <Tab label="Property Enquiries" />
            <Tab label="Utilities" />
            <Tab label="Assessments" />
            <Tab label="Insurance" />
            <Tab label="Ownership" />
          </Tabs>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 2, flexShrink: 0 }}>
            {neighborUnitIds.prev && (
              <Button
                variant="outlined"
                onClick={() => navigate(`/units/${neighborUnitIds.prev}/details`)}
              >
                &lt; Previous Unit
              </Button>
            )}
            {neighborUnitIds.next && (
              <Button
                variant="outlined"
                onClick={() => navigate(`/units/${neighborUnitIds.next}/details`)}
              >
                Next Unit &gt;
              </Button>
            )}
            {canEdit && <InviteOwnersButton unitId={unitId} />}
            {canEdit && (
              <Button
                variant="contained"
                onClick={() => navigate(`/units/${unitId}/edit`)}
              >
                Edit Unit
              </Button>
            )}
          </Box>
        </Box>

        {activeTab === 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={unit.has_different_correspondence_address ? 4 : 6}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  Unit Details
                </Typography>
                <Box component="table" sx={detailTableSx}>
                  <tbody>
                    <tr>
                      <th>Unit Code:</th>
                      <td>{unit.unit_code || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Unit Name:</th>
                      <td>{unit.unit_name || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Floor:</th>
                      <td>{unit.floor || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Size:</th>
                      <td>
                        {unit.square_feet
                          ? `${unit.square_feet} sqft${formatSqMeters(unit.square_feet)}`
                          : "Unknown"}
                      </td>
                    </tr>
                    <tr>
                      <th>Service Charge:</th>
                      <td>{formatCurrency(unit.service_charge)}</td>
                    </tr>
                    <tr>
                      <th>Owner Name:</th>
                      <td>{unit.owner_name || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Mobile:</th>
                      <td>{unit.mobile || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Email:</th>
                      <td>
                        {unit.email ? (
                          <a href={`mailto:${unit.email}`} style={{ color: "inherit" }}>
                            {unit.email}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Created:</th>
                      <td>{formatDate(unit.created_at_utc)}</td>
                    </tr>
                  </tbody>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={unit.has_different_correspondence_address ? 4 : 6}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Unit Address
                  </Typography>
                  <Tooltip title="Copy to clipboard">
                    <IconButton
                      size="small"
                      aria-label="Copy unit address"
                      onClick={() =>
                        handleCopy(
                          buildAddress([
                            unit.owner_name,
                            unit.address_line1,
                            unit.address_line2,
                            unit.city,
                            unit.county,
                            unit.postcode,
                          ]),
                          "Unit address"
                        )
                      }
                    >
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box component="table" sx={detailTableSx}>
                  <tbody>
                    {unit.owner_name && (
                      <tr>
                        <th>Owner Name:</th>
                        <td>{unit.owner_name}</td>
                      </tr>
                    )}
                    {unit.address_line1 && (
                      <tr>
                        <th>Address Line 1:</th>
                        <td>{unit.address_line1}</td>
                      </tr>
                    )}
                    {unit.address_line2 && (
                      <tr>
                        <th>Address Line 2:</th>
                        <td>{unit.address_line2}</td>
                      </tr>
                    )}
                    {unit.city && (
                      <tr>
                        <th>City:</th>
                        <td>{unit.city}</td>
                      </tr>
                    )}
                    {unit.county && (
                      <tr>
                        <th>County:</th>
                        <td>{unit.county}</td>
                      </tr>
                    )}
                    {unit.postcode && (
                      <tr>
                        <th>Postcode:</th>
                        <td>{unit.postcode}</td>
                      </tr>
                    )}
                  </tbody>
                </Box>
              </Paper>
            </Grid>

            {unit.has_different_correspondence_address && (
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="h6" fontWeight={600}>
                      Correspondence Address
                    </Typography>
                    <Tooltip title="Copy to clipboard">
                      <IconButton
                        size="small"
                        aria-label="Copy correspondence address"
                        onClick={() =>
                          handleCopy(
                            buildAddress([
                              unit.owner_name,
                              unit.correspondence_address_line1,
                              unit.correspondence_address_line2,
                              unit.correspondence_city,
                              unit.correspondence_county,
                              unit.correspondence_postcode,
                            ]),
                            "Correspondence address"
                          )
                        }
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box component="table" sx={detailTableSx}>
                    <tbody>
                      {unit.owner_name && (
                        <tr>
                          <th>Owner Name:</th>
                          <td>{unit.owner_name}</td>
                        </tr>
                      )}
                      {unit.correspondence_address_line1 && (
                        <tr>
                          <th>Address Line 1:</th>
                          <td>{unit.correspondence_address_line1}</td>
                        </tr>
                      )}
                      {unit.correspondence_address_line2 && (
                        <tr>
                          <th>Address Line 2:</th>
                          <td>{unit.correspondence_address_line2}</td>
                        </tr>
                      )}
                      {unit.correspondence_city && (
                        <tr>
                          <th>City:</th>
                          <td>{unit.correspondence_city}</td>
                        </tr>
                      )}
                      {unit.correspondence_county && (
                        <tr>
                          <th>County:</th>
                          <td>{unit.correspondence_county}</td>
                        </tr>
                      )}
                      {unit.correspondence_postcode && (
                        <tr>
                          <th>Postcode:</th>
                          <td>{unit.correspondence_postcode}</td>
                        </tr>
                      )}
                    </tbody>
                  </Box>
                </Paper>
              </Grid>
            )}
            <Grid item xs={12} md={unit.has_different_correspondence_address ? 12 : 6}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Recent Ownership</Typography>
                  </Box>
                  <Button variant="outlined" onClick={() => setSearchParams({ tab: "ownership" })}>
                    + View all
                  </Button>
                </Box>
                {recentOwnerships.length ? (
                  <TableContainer><Table size="small"><TableHead><TableRow>
                    <TableCell>Owner</TableCell><TableCell>From</TableCell><TableCell>To</TableCell>
                  </TableRow></TableHead><TableBody>
                    {recentOwnerships.map((ownership) => <TableRow key={ownership.id}>
                      <TableCell>{ownership.owner_name}</TableCell>
                      <TableCell>{formatDate(ownership.effective_from)}</TableCell>
                      <TableCell>{ownership.effective_to ? formatDate(ownership.effective_to) : "Current"}</TableCell>
                    </TableRow>)}
                  </TableBody></Table></TableContainer>
                ) : <Typography color="text.secondary">No ownership records have been recorded.</Typography>}
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <SectionLPE1
            lpe1Forms={lpe1Forms}
            fme1Forms={fme1Forms}
            navigate={navigate}
            location={location}
            formatDate={formatDate}
            onGenerateLPE1={handleGenerateLPE1}
            onGenerateFME1={handleGenerateFME1}
          />
        )}

        {activeTab === 2 && (
          <SectionCard
            title="Utilities"
            items={utilities}
            canManage={canEdit}
            manageLink={`/units/${unitId}/utilities`}
            columns={[
              ["Type", "utility_type"],
              ["Supplier", "supplier_name"],
              ["Email", "email_address"],
              ["Contact", "contact_number"],
              ["Meter #", "meter_number"],
            ]}
          />
        )}

        {activeTab === 3 && (
          <SectionCard
            title="Assessments"
            items={assessments}
            canManage={canEdit}
            manageLink={`/units/${unitId}/assessments`}
            columns={[
              ["Type", (row) => getAssessmentType(row.assessment_type_id)],
              [
                "Pass",
                (row) => (
                  <Chip
                    label={row.passed ? "Yes" : "No"}
                    color={row.passed ? "success" : "error"}
                  />
                ),
              ],
              ["Assessment Date", (row) => formatDate(row.assessment_date)],
              ["Expiry", (row) => formatDate(row.expiry_date)],
            ]}
            dateField="expiry_date"
          />
        )}

        {activeTab === 4 && (
          <SectionCard
            title="Insurance Policies"
            items={insurance_policies}
            canManage={canEdit}
            manageLink={`/units/${unitId}/insurance`}
            columns={[
              ["Policy #", "policy_number"],
              ["Provider", "provider_name"],
              ["Coverage", "coverage_type"],
              ["Renewal", (row) => formatDate(row.renewal_date)],
              ["Premium (£)", "premium_amount"],
            ]}
            dateField="renewal_date"
          />
        )}
        {activeTab === 5 && (
          <OwnershipPanel
            unitId={unitId}
            unit={unit}
            canEdit={canEdit}
            onToast={showToast}
            onOwnerChanged={(ownership) => {
              setData((current) => ({
                ...current,
                unit: {
                  ...current.unit,
                  owner_name: ownership.owner_name,
                  email: ownership.email,
                  mobile: ownership.mobile,
                  correspondence_address_line1: ownership.correspondence_address_line1,
                  correspondence_address_line2: ownership.correspondence_address_line2,
                  correspondence_city: ownership.correspondence_city,
                  correspondence_county: ownership.correspondence_county,
                  correspondence_postcode: ownership.correspondence_postcode,
                },
              }));
              getUnitOwnerships(unitId, 1, 5).then((response) =>
                setRecentOwnerships(response.results || [])
              );
            }}
            onOwnershipHistoryChanged={() => getUnitOwnerships(unitId, 1, 5).then((response) =>
              setRecentOwnerships(response.results || [])
            )}
          />
        )}

      </Box>

      {/* TOAST / SNACKBAR */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

/* --------------------------------------------------
   LPE1 Forms Section with Collapse
-------------------------------------------------- */
function SectionLPE1({ lpe1Forms, fme1Forms, navigate, location, formatDate, onGenerateLPE1, onGenerateFME1 }) {
  const [expanded, setExpanded] = useState(false);
  const INITIAL_COUNT = 5;
  
  const visibleForms = expanded ? lpe1Forms : lpe1Forms.slice(0, INITIAL_COUNT);
  const hasMore = lpe1Forms.length > INITIAL_COUNT;
  const hiddenCount = lpe1Forms.length - INITIAL_COUNT;
  const visibleFme1Forms = expanded ? fme1Forms : fme1Forms.slice(0, INITIAL_COUNT);
  const hasMoreFme1 = fme1Forms.length > INITIAL_COUNT;
  const hiddenFme1Count = fme1Forms.length - INITIAL_COUNT;

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={600}>
          Property Enquiries
          {lpe1Forms.length > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({lpe1Forms.length} total)
            </Typography>
          )}
        </Typography>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={onGenerateLPE1}>
            + New LPE1
          </Button>
          <Button variant="outlined" onClick={onGenerateFME1}>
            + New FME1
          </Button>
        </Stack>
      </Grid>

      {lpe1Forms.length > 0 || fme1Forms.length > 0 ? (
        <>
          <EnquiryTable
            title="LPE1 Forms"
            forms={visibleForms}
            formatDate={formatDate}
            onOpen={(formId) => navigate(`/lpe1/${formId}`, { state: { from: location.pathname } })}
          />

          {hasMore && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Button size="small" onClick={() => setExpanded(!expanded)} sx={{ textTransform: "none" }}>
                {expanded ? "Show less LPE1s" : `Show ${hiddenCount} more LPE1s`}
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <EnquiryTable
              title="FME1 Forms"
              forms={visibleFme1Forms}
              formatDate={formatDate}
              onOpen={(formId) => navigate(`/fme1/${formId}`, { state: { from: location.pathname } })}
            />
          </Box>

          {hasMoreFme1 && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Button size="small" onClick={() => setExpanded(!expanded)} sx={{ textTransform: "none" }}>
                {expanded ? "Show less FME1s" : `Show ${hiddenFme1Count} more FME1s`}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Typography>No property enquiry forms created yet.</Typography>
      )}
    </Paper>
  );
}

function EnquiryTable({ title, forms, formatDate, onOpen }) {
  return (
    <>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        {title}
      </Typography>
      {forms.length > 0 ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Requested Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Due By Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {forms.map((form) => (
                <TableRow key={`${title}-${form.id}`}>
                  <TableCell>{formatDate(form.requested_date)}</TableCell>
                  <TableCell>{formatDate(form.due_by_date)}</TableCell>
                  <TableCell>{form.status || "—"}</TableCell>
                  <TableCell>{formatDate(form.updated_at_utc)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => onOpen(form.id)}>
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography color="text.secondary">No {title.toLowerCase()} created yet.</Typography>
      )}
    </>
  );
}

/* --------------------------------------------------
   Reusable Section
-------------------------------------------------- */
function SectionCard({ title, items, canManage, manageLink, columns, dateField }) {
  const navigate = useNavigate();

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>

        {canManage && (
          <Button variant="outlined" onClick={() => navigate(manageLink)}>
            Manage →
          </Button>
        )}
      </Grid>

      {items?.length > 0 ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map(([label]) => (
                  <TableCell key={label} sx={{ fontWeight: 600 }}>
                    {label}
                  </TableCell>
                ))}
                {dateField && <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {items.map((row) => {
                const docStatus = dateField ? getDocumentStatus(row[dateField]) : null;
                
                return (
                  <TableRow key={row.id}>
                    {columns.map(([_, field]) => (
                      <TableCell key={row.id + String(_)}>
                        {typeof field === "function" ? field(row) : row[field]}
                      </TableCell>
                    ))}
                    {dateField && (
                      <TableCell>
                        <Chip
                          icon={docStatus.icon}
                          label={docStatus.label}
                          color={docStatus.color}
                          size="small"
                          variant={docStatus.status === "valid" ? "outlined" : "filled"}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography>No {title.toLowerCase()} found.</Typography>
      )}
    </Paper>
  );
}

const formatHandoverCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "N/A";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

function TransferHandover({ unitId, completionDate }) {
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    if (!completionDate) return undefined;
    let cancelled = false;
    setState({ status: "loading", data: null });
    getUnitTransferHandover(unitId, completionDate)
      .then((res) => !cancelled && setState({ status: "ready", data: res.data }))
      .catch((err) => !cancelled && setState({
        status: err.response?.status === 403 ? "forbidden" : "error",
        data: null,
      }));
    return () => { cancelled = true; };
  }, [unitId, completionDate]);

  if (!completionDate) return null;
  if (state.status === "loading") return <CircularProgress size={20} aria-label="Loading service charge handover" />;
  if (state.status === "forbidden") {
    return <Alert severity="info">Service charge handover figures are shown to finance users.</Alert>;
  }
  if (state.status === "error") return <Alert severity="warning">Could not load the service charge handover.</Alert>;

  const h = state.data;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Service charge handover</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        For the conveyancers&apos; completion statement. Nothing is posted to the ledger; the account stays with the unit.
      </Typography>
      <Typography variant="body2">Balance before completion: {formatHandoverCurrency(h.balance_before_completion)}</Typography>
      {h.fiscal_year && <>
        <Typography variant="body2">
          Year {formatDateUk(h.fiscal_year.start_date)} to {formatDateUk(h.fiscal_year.end_date)}: {formatHandoverCurrency(h.annual_charge)}
          {h.charge_basis === "estimate" ? " (estimate)" : ""}
        </Typography>
        <Typography variant="body2">Seller share: {formatHandoverCurrency(h.seller_share)} ({h.seller_days} of {h.days_in_year} days)</Typography>
        <Typography variant="body2">Buyer share: {formatHandoverCurrency(h.buyer_share)} ({h.buyer_days} of {h.days_in_year} days)</Typography>
        <Alert severity="warning" sx={{ mt: 1 }}>
          Year-end balancing charges or credits go to whoever owns the unit when they are issued. The solicitors may hold a retention from the sale proceeds to cover them.
        </Alert>
        <Alert severity="warning" sx={{ mt: 1 }}>
          Reserve fund contributions are not normally refunded on sale. Check the lease.
        </Alert>
      </>}
      {h.warnings.map((warning) => (
        <Alert key={warning} severity="warning" sx={{ mt: 1 }}>{warning}</Alert>
      ))}
      <Link component={RouterLink} to={`/units/${unitId}/statement`} target="_blank" rel="noopener" sx={{ display: "inline-block", mt: 1 }}>
        View unit statement
      </Link>
    </Paper>
  );
}

function OwnershipPanel({ unitId, unit, canEdit, onToast, onOwnerChanged, onOwnershipHistoryChanged }) {
  const [data, setData] = useState({ results: [], total: 0, page: 1, page_size: 10, current: null });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOwnership, setEditingOwnership] = useState(null);
  const [ownershipCorrection, setOwnershipCorrection] = useState({});
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    owner_name: "", email: "", mobile: "", correspondence_address_line1: "",
    correspondence_address_line2: "", correspondence_city: "", correspondence_county: "",
    correspondence_postcode: "", effective_from: new Date().toISOString().slice(0, 10), correspondence_same_as_unit: true,
  });

  const load = async (page = 1) => {
    setLoading(true);
    try {
      setData(await getUnitOwnerships(unitId, page));
    } catch (err) {
      onToast(err.response?.data?.error || "Could not load ownership history.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTransfer = () => {
    const current = data.current;
    setForm({
      owner_name: "", email: "", mobile: "", correspondence_address_line1: "",
      correspondence_address_line2: "", correspondence_city: "", correspondence_county: "",
      correspondence_postcode: "", effective_from: new Date().toISOString().slice(0, 10), correspondence_same_as_unit: true,
      previous_owner: current?.owner_name || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { correspondence_same_as_unit, ...ownerForm } = form;
      const payload = correspondence_same_as_unit
        ? {
            ...ownerForm,
            correspondence_address_line1: unit.address_line1 || "",
            correspondence_address_line2: unit.address_line2 || "",
            correspondence_city: unit.city || "",
            correspondence_county: unit.county || "",
            correspondence_postcode: unit.postcode || "",
          }
        : ownerForm;
      const result = await changeUnitOwner(unitId, payload);
      if (result.ownership.is_current) onOwnerChanged(result.ownership);
      else onOwnershipHistoryChanged();
      setDialogOpen(false);
      onToast("Owner changed");
      load();
    } catch (err) {
      onToast(err.response?.data?.error || "Could not change owner.", "error");
    } finally {
      setSaving(false);
    }
  };

  const update = (event) => setForm((old) => ({ ...old, [event.target.name]: event.target.value }));
  const updateCorrection = (event) => setOwnershipCorrection((old) => ({ ...old, [event.target.name]: event.target.value }));
  const saveDateCorrection = async () => {
    setSaving(true);
    try {
      const result = await updateUnitOwnership(unitId, editingOwnership.id, {
        ...ownershipCorrection,
      });
      if (result.ownership.is_current) onOwnerChanged(result.ownership);
      else onOwnershipHistoryChanged();
      setEditingOwnership(null);
      onToast("Owner details updated");
      load(data.page);
    } catch (err) {
      onToast(err.response?.data?.error || "Could not update the ownership date.", "error");
    } finally {
      setSaving(false);
    }
  };
  const totalPages = Math.max(Math.ceil(data.total / data.page_size), 1);
  const maximumEffectiveDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Ownership</Typography>
          <Typography variant="body2" color="text.secondary">Current and historic legal owners</Typography>
        </Box>
        {canEdit && <Button variant="outlined" onClick={openTransfer}>+ Change owner</Button>}
      </Stack>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress aria-label="Loading ownership" />
        </Box>
      ) : data.current ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={600}>{data.current.owner_name}</Typography>
          <Typography variant="body2">Owner since {formatDateUk(data.current.effective_from)}</Typography>
          {data.current.email && <Typography variant="body2">{data.current.email}</Typography>}
        </Paper>
      ) : <Alert severity="info" sx={{ mb: 3 }}>No current owner has been recorded.</Alert>}
      {!loading && (
        <>
          <TableContainer><Table size="small"><TableHead><TableRow>
            <TableCell>Owner</TableCell><TableCell>Effective from</TableCell><TableCell>Effective to</TableCell><TableCell>Recorded</TableCell>{canEdit && <TableCell>Actions</TableCell>}
          </TableRow></TableHead><TableBody>
            {data.results.map((row) => <TableRow key={row.id}>
              <TableCell>{row.owner_name}</TableCell><TableCell>{formatDateUk(row.effective_from)}</TableCell>
              <TableCell>{row.effective_to ? formatDateUk(row.effective_to) : "Current"}</TableCell><TableCell>{formatDateUk(row.recorded_at)}</TableCell>
              {canEdit && <TableCell><Button variant="outlined" size="small" onClick={() => { setEditingOwnership(row); setOwnershipCorrection({ owner_name: row.owner_name || "", email: row.email || "", mobile: row.mobile || "", correspondence_address_line1: row.correspondence_address_line1 || "", correspondence_address_line2: row.correspondence_address_line2 || "", correspondence_city: row.correspondence_city || "", correspondence_county: row.correspondence_county || "", correspondence_postcode: row.correspondence_postcode || "", effective_from: row.effective_from }); }}>✎ Correct owner</Button></TableCell>}
            </TableRow>)}
          </TableBody></Table></TableContainer>
          {data.total > data.page_size && <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mt: 2 }}>
            <Button variant="outlined" disabled={data.page <= 1} onClick={() => load(data.page - 1)}>‹ Previous</Button>
            <Typography variant="body2">Page {data.page} of {totalPages}</Typography>
            <Button variant="outlined" disabled={data.page >= totalPages} onClick={() => load(data.page + 1)}>Next ›</Button>
          </Stack>}
        </>
      )}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change owner</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
          <TextField required label="Owner name" name="owner_name" value={form.owner_name} onChange={update} autoFocus />
          <TextField required type="date" label="Effective transfer date" name="effective_from" value={form.effective_from} onChange={update} InputLabelProps={{ shrink: true }} inputProps={{ max: maximumEffectiveDate }} helperText="Up to seven days in the future." />
          <TextField label="Email" name="email" value={form.email} onChange={update} />
          <TextField label="Mobile" name="mobile" value={form.mobile} onChange={update} />
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2">Unit address</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {[unit.address_line1, unit.address_line2, unit.city, unit.county, unit.postcode].filter(Boolean).join("\n") || "No unit address recorded"}
            </Typography>
          </Paper>
          <FormControlLabel
            control={<Checkbox checked={form.correspondence_same_as_unit} onChange={(event) => setForm((old) => ({ ...old, correspondence_same_as_unit: event.target.checked }))} />}
            label="Correspondence address is the same as the unit address"
          />
          {!form.correspondence_same_as_unit && <>
            <TextField label="Correspondence address line 1" name="correspondence_address_line1" value={form.correspondence_address_line1} onChange={update} />
            <TextField label="Correspondence address line 2" name="correspondence_address_line2" value={form.correspondence_address_line2} onChange={update} />
            <TextField label="City" name="correspondence_city" value={form.correspondence_city} onChange={update} />
            <TextField label="County" name="correspondence_county" value={form.correspondence_county} onChange={update} />
            <TextField label="Postcode" name="correspondence_postcode" value={form.correspondence_postcode} onChange={update} />
          </>}
          {data.current && <TransferHandover unitId={unitId} completionDate={form.effective_from} />}
        </Stack></DialogContent>
        <DialogActions><Button variant="outlined" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={save} disabled={saving || !form.owner_name}>+ Save owner</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(editingOwnership)} onClose={() => !saving && setEditingOwnership(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Correct owner details</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
          <TextField fullWidth required label="Owner name" name="owner_name" value={ownershipCorrection.owner_name || ""} onChange={updateCorrection} />
          <TextField fullWidth required type="date" label="Effective date" name="effective_from" value={ownershipCorrection.effective_from || ""} onChange={updateCorrection} InputLabelProps={{ shrink: true }} inputProps={{ max: maximumEffectiveDate }} helperText="Adjacent ownership periods will be adjusted automatically." />
          <TextField fullWidth label="Email" name="email" value={ownershipCorrection.email || ""} onChange={updateCorrection} />
          <TextField fullWidth label="Mobile" name="mobile" value={ownershipCorrection.mobile || ""} onChange={updateCorrection} />
          <TextField fullWidth label="Correspondence address line 1" name="correspondence_address_line1" value={ownershipCorrection.correspondence_address_line1 || ""} onChange={updateCorrection} />
          <TextField fullWidth label="Correspondence address line 2" name="correspondence_address_line2" value={ownershipCorrection.correspondence_address_line2 || ""} onChange={updateCorrection} />
          <TextField fullWidth label="City" name="correspondence_city" value={ownershipCorrection.correspondence_city || ""} onChange={updateCorrection} />
          <TextField fullWidth label="County" name="correspondence_county" value={ownershipCorrection.correspondence_county || ""} onChange={updateCorrection} />
          <TextField fullWidth label="Postcode" name="correspondence_postcode" value={ownershipCorrection.correspondence_postcode || ""} onChange={updateCorrection} />
          {editingOwnership && ownershipCorrection.effective_from && ownershipCorrection.effective_from !== editingOwnership.effective_from && (
            <TransferHandover unitId={unitId} completionDate={ownershipCorrection.effective_from} />
          )}
        </Stack></DialogContent>
        <DialogActions><Button variant="outlined" onClick={() => setEditingOwnership(null)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={saveDateCorrection} disabled={saving || !ownershipCorrection.owner_name || !ownershipCorrection.effective_from}>+ Save correction</Button></DialogActions>
      </Dialog>
    </Paper>
  );
}

export default UnitDetailsPage;
